import * as React from "react";
import { Environments, SystemError } from "@emisgroup/cohort-builder-kit";

/**
 * Error boundary configuration parameter
 */
export interface IErrorBoundaryConfig {
  /**
   * Error message to be displayed in case of page crash
   */
  errorMessage: string;

  /**
   * Flag to enable/disable the default page in case of error
   */
  showMessage: boolean;
}

/**
 * Type of Error factory
 */
export interface IErrorFactory {
  /**
   * Provides a call back mechanism , so that modules can log errors accordingly
   */
  handleError: (error: string) => void;
}

/**
 * Setting default values for the config
 */
const defaultConfig: IErrorBoundaryConfig = {
  errorMessage: "An error has occurred",
  showMessage: false
};

interface IDerivedState {
  /**
   * Flag that gets enabled when error occurs in the application
   */
  hasError: boolean;
}

interface IErrorState extends IDerivedState {
  /**
   * Actual error
   */
  error?: Error;
}

type ErrorBoundaryChildren = {
  children: React.ReactNode;
};

type IErrorProps =
  IErrorFactory & Partial<IErrorBoundaryConfig> & ErrorBoundaryChildren & { fallback?: React.ReactNode };

export class ErrorBoundary extends React.Component<IErrorProps, IErrorState> {
  constructor(props: IErrorProps) {
    super(props);
    this.state = {
      hasError: false
    };
  }

  public static defaultProps: IErrorBoundaryConfig = defaultConfig;

  /**
   * Hook to render a fallback UI after an error has been thrown
   */
  public static getDerivedStateFromError(): IDerivedState {
    return { hasError: true };
  }

  public componentDidCatch(error: Error): void {
    const { handleError } = this.props;
    this.setState({
      error: error
    });
    /**
     * Invoking the handler function that has been provided by modules
     */
    if (typeof handleError === "function") {
      handleError.call(this, error);
    }
  }

  public render(): React.ReactNode {
    const { errorMessage, showMessage } = this.props;
    const env = process.env.APP_TARGET_ENV as Environments;
    const debugEnvs: Environments[] = ["dev", "sbx"];

    if (this.state.hasError && showMessage) {
      // If we're in a debug environment (dev or sbx), show the actual error
      let errorInfo: string | undefined = undefined;
      if (this.state.error && debugEnvs.includes(env)) {
        errorInfo = this.state.error.message;
      }

      if(this.props.fallback) {
        return (this.props.fallback);
      }

      return (
        <SystemError information={errorInfo} message={errorMessage} />
      );
    }
    return <React.Fragment>{this.props.children}</React.Fragment>;
  }
}

export default ErrorBoundary;
